import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import Aura from '@primeuix/themes/aura';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';

import { AuthService, GrDialogService, GrToastService } from '@common/services';
import { bearerInterceptor } from '@common/interceptors';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      // Settings sections take the entity id as a component input, so the route param binds
      // straight to it - and `always` is what lets a child section see the shell's `:accountId`.
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      // Runs in an injection context. A failed lazy chunk (usually a stale tab after a
      // deploy) used to leave the user on a dead screen with no explanation.
      withNavigationErrorHandler((navigationError) => {
        // The toast is deliberately generic, but swallowing the cause made a stale
        // chunk indistinguishable from a genuinely broken route. Log the real one.
        console.error(
          `[router] navigation to "${navigationError.url}" failed:`,
          navigationError.error,
        );
        inject(GrToastService).error(
          'That page could not be loaded. Reload the app — it may have been updated since you opened this tab.',
          'Navigation failed',
        );
      }),
    ),

    provideHttpClient(withInterceptors([bearerInterceptor])),
    provideAppInitializer(() => inject(AuthService).initialize()),

    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
      ripple: false,
    }),
    { provide: DialogService, useClass: GrDialogService },
    // GrToastService is the only thing that should talk to MessageService; it needs a root
    // instance because the app-level <gr-toast> outlet lives outside any component provider.
    MessageService,
  ],
};
